/**
 * Kanban Column Component
 * Status column for kanban board with droppable area
 */

import React from 'react';
import { useDroppable } from '@dnd-kit/core';

import { TicketListItem, TicketStatus } from '@/types';
import { KanbanCard } from './KanbanCard';
import { formatLabel } from '@/utils/helpers';
import { Plus } from 'lucide-react';

interface KanbanColumnProps {
  status: TicketStatus;
  tickets: TicketListItem[];
  onAddTicket?: (status: TicketStatus) => void;
  compact?: boolean;
}

export function KanbanColumn({ status, tickets, onAddTicket, compact = false }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  const getColumnColor = (status: TicketStatus): string => {
    const colors = {
      [TicketStatus.NEW]: 'bg-purple-50 border-purple-200',
      [TicketStatus.TRIAGED]: 'bg-blue-50 border-blue-200',
      [TicketStatus.IN_PROGRESS]: 'bg-indigo-50 border-indigo-200',
      [TicketStatus.WAITING_CUSTOMER]: 'bg-amber-50 border-amber-200',
      [TicketStatus.WAITING_INTERNAL]: 'bg-yellow-50 border-yellow-200',
      [TicketStatus.IN_REVIEW]: 'bg-cyan-50 border-cyan-200',
      [TicketStatus.RESOLVED]: 'bg-green-50 border-green-200',
      [TicketStatus.CLOSED]: 'bg-gray-50 border-gray-200',
      [TicketStatus.REOPENED]: 'bg-red-50 border-red-200',
      [TicketStatus.CANCELLED]: 'bg-gray-50 border-gray-200',
    };
    return colors[status] || colors[TicketStatus.NEW];
  };

  return (
    <div className={`flex flex-col h-full ${compact ? 'min-w-[220px] max-w-[220px]' : 'min-w-[320px] max-w-[320px]'}`}>
      {/* Column Header */}
      <div className={`rounded-t-lg border-2 p-3 ${getColumnColor(status)}`}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-gray-900">{formatLabel(status)}</h3>
          <span className="text-sm text-gray-600 bg-white px-2 py-0.5 rounded-full font-medium">
            {tickets.length}
          </span>
        </div>
        {onAddTicket && (
          <button
            onClick={() => onAddTicket(status)}
            className="w-full mt-2 flex items-center justify-center py-1.5 border border-gray-300 rounded bg-white hover:bg-gray-50 text-sm text-gray-600 transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add ticket
          </button>
        )}
      </div>

      {/* Droppable Area */}
      <div
        ref={setNodeRef}
        className={`flex-1 border-x-2 border-b-2 ${getColumnColor(
          status
        )} rounded-b-lg p-3 overflow-y-auto ${
          isOver ? 'ring-2 ring-primary-500 ring-inset' : ''
        }`}
      >
        <div className="space-y-3 min-h-[60px]">
            {tickets.map((ticket) => (
              <KanbanCard key={ticket.id} ticket={ticket} compact={compact} />
            ))}
            {tickets.length === 0 && !isOver && (
              <div className="text-center py-8 text-gray-400 text-sm">
                Drop tickets here
              </div>
            )}
          </div>
      </div>
    </div>
  );
}
